import { useEffect, useRef, useState, type ReactNode } from 'react';

interface Props {
  /** Identity of the currently-shown view. Changing this triggers a cross-fade. */
  viewKey: string;
  /** Mobile drawer height. 'compact' for list, 'tall' for detail. */
  mobileSize?: 'compact' | 'tall';
  children: ReactNode;
}

/**
 * Persistent drawer that cross-fades its contents when `viewKey` changes.
 * Layout:
 *   - Mobile (<md): bottom sheet, full width, ~60% height, rounded top.
 *   - Desktop (≥md): right panel, 420px wide, full height, rounded left.
 */
export function Drawer({ viewKey, mobileSize = 'tall', children }: Props) {
  const [active, setActive] = useState({ key: viewKey, node: children });
  const [phase, setPhase] = useState<'in' | 'out'>('in');
  const pendingRef = useRef<{ key: string; node: ReactNode } | null>(null);

  useEffect(() => {
    if (viewKey === active.key) {
      setActive({ key: viewKey, node: children });
      return;
    }
    pendingRef.current = { key: viewKey, node: children };
    setPhase('out');
    const t = setTimeout(() => {
      if (pendingRef.current) {
        setActive(pendingRef.current);
        pendingRef.current = null;
      }
      setPhase('in');
    }, 180);
    return () => clearTimeout(t);
  }, [viewKey, children, active.key]);

  return (
    <aside
      className={[
        'fixed z-10 pointer-events-auto',
        // Mobile: bottom sheet — compact for list, tall for detail.
        'bottom-0 left-0 right-0 transition-[height] duration-500 ease-out',
        mobileSize === 'compact' ? 'h-[38dvh] max-h-[400px]' : 'h-[50dvh] max-h-[540px]',
        // Desktop overrides
        'md:top-0 md:right-0 md:left-auto md:bottom-0 md:h-full md:w-[420px] md:max-h-none md:max-w-[92vw]'
      ].join(' ')}
    >
      <div
        className={[
          'glass h-full shadow-panel overflow-hidden',
          'rounded-t-3xl md:rounded-t-none md:rounded-l-3xl'
        ].join(' ')}
      >
        <div
          className={[
            'h-full transition-all ease-out',
            phase === 'out'
              ? 'opacity-0 translate-y-1 duration-[180ms]'
              : 'opacity-100 translate-y-0 duration-[220ms]'
          ].join(' ')}
        >
          {active.node}
        </div>
      </div>
    </aside>
  );
}
